import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const AutocompleteInput = ({
  value,
  onChange,
  suggestions = [],
  disabled = false,
  className = "",
  placeholder = "",
  isTextarea = false,
  onSuggestionSelect,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, showAbove: false, maxListHeight: 240 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const DROPDOWN_GAP = 0;
  const MIN_DROPDOWN_HEIGHT = 200;

  // Filter suggestions based on user input
  useEffect(() => {
    if (!suggestions || suggestions.length === 0) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // If there's a value, filter suggestions by it
    if (value && value.toString().trim()) {
      const searchTerm = value.toString().toLowerCase();
      const filtered = suggestions.filter(suggestion =>
        suggestion.toString().toLowerCase().includes(searchTerm)
      );
      setFilteredSuggestions(filtered);
    } else {
      // If no value, show all suggestions
      setFilteredSuggestions(suggestions);
    }
  }, [suggestions, value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.autocomplete-dropdown-portal');
      const clickedInsideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInsideDropdown = dropdown && dropdown.contains(event.target);
      
      if (!clickedInsideWrapper && !clickedInsideDropdown) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSuggestions]);

  // Auto-resize textarea to fit content (works for both editable and disabled states)
  useEffect(() => {
    if (isTextarea && inputRef.current) {
      const textarea = inputRef.current;
      // Use requestAnimationFrame to ensure DOM is ready, especially for disabled textareas
      requestAnimationFrame(() => {
        // Reset height to auto to get accurate scrollHeight
        textarea.style.height = "auto";
        // Set height to scrollHeight to fit all content
        const newHeight = textarea.scrollHeight;
        textarea.style.height = `${newHeight}px`;
      });
    }
  }, [value, isTextarea, disabled]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // If it's a textarea and Enter is pressed without suggestions showing, allow default behavior (newline)
    if (isTextarea && e.key === "Enter" && (!showSuggestions || filteredSuggestions.length === 0)) {
      return; // Allow default Enter behavior for newlines in textarea
    }

    // Show suggestions on any key input if they exist
    if (suggestions && suggestions.length > 0 && !showSuggestions) {
      setFilteredSuggestions(suggestions);
      setShowSuggestions(true);
    }

    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          handleSuggestionClick(filteredSuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onChange({ target: { value: suggestion } });
    // Call onSuggestionSelect callback if provided
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    onChange(e);
    setHighlightedIndex(-1);
    // Auto-resize textarea on input change
    if (isTextarea && e.target) {
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
  };

  const handleFocus = () => {
    // Always show all suggestions on focus if there are any available
    if (suggestions && suggestions.length > 0) {
      setFilteredSuggestions(suggestions);
      setShowSuggestions(true);
      updateDropdownPosition();
    }
  };

  // Update dropdown position based on input field position (viewport coords for fixed)
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
      const spaceAbove = rect.top - DROPDOWN_GAP;
      const showAbove = spaceBelow < MIN_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
      const maxListHeight = showAbove
        ? Math.min(240, Math.max(100, spaceAbove))
        : Math.min(240, Math.max(100, spaceBelow));
      const left = Math.max(0, rect.left);
      const width = Math.min(rect.width, window.innerWidth - left);
      setDropdownPosition({
        top: showAbove ? undefined : rect.bottom + DROPDOWN_GAP,
        bottom: showAbove ? window.innerHeight - rect.top + DROPDOWN_GAP : undefined,
        left,
        width,
        showAbove,
        maxListHeight
      });
    }
  };

  // Update position when suggestions are shown, filtered, or window is resized/scrolled
  useEffect(() => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showSuggestions, filteredSuggestions.length, value]);

  // Scroll highlighted suggestion into view when using keyboard
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex];
      if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedIndex]);

  const InputComponent = isTextarea ? "textarea" : "input";

  // Add whitespace preservation styles for textarea
  // Replace overflow-hidden with overflow-y-hidden to allow auto-resize while preventing scrollbars
  const textareaClassName = isTextarea 
    ? `${className} whitespace-pre-wrap`.replace(/overflow-hidden/g, "overflow-y-hidden")
    : className;

  return (
    <div ref={wrapperRef} className="relative w-full">
      <InputComponent
        ref={inputRef}
        type={isTextarea ? undefined : "text"}
        value={value ?? ""}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        disabled={disabled}
        className={textareaClassName}
        placeholder={placeholder}
        autoComplete="off"
        {...props}
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && createPortal(
        <div 
          className="autocomplete-dropdown-portal fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden"
          style={{
            ...(dropdownPosition.top != null ? { top: `${dropdownPosition.top}px` } : {}),
            ...(dropdownPosition.bottom != null ? { bottom: `${dropdownPosition.bottom}px` } : {}),
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxWidth: "100vw"
          }}
        >
          <div
            ref={listRef}
            className="overflow-y-auto overflow-x-hidden"
            style={{ maxHeight: `${dropdownPosition.maxListHeight}px` }}
          >
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`px-3 py-2 cursor-pointer transition-colors truncate ${
                  index === highlightedIndex
                    ? "bg-blue-100"
                    : "hover:bg-gray-100"
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AutocompleteInput;
